import { CaretRight } from "@phosphor-icons/react";
import * as Accordion from "@radix-ui/react-accordion";
import { Text } from "@radix-ui/themes";

import { getBlockKey } from "../blockKey";
import { CanvasBlockRenderer } from "../CanvasBlockRenderer";
import { type CanvasBlock } from "../types";

interface SectionBlockProps {
  id: string;
  title: string;
  children: CanvasBlock[];
  onSectionClick?: (sectionId: string, title: string) => void;
  onBadgeAction?: (action: string, label: string) => void;
}

export function SectionBlock({
  id,
  title,
  children,
  onSectionClick,
  onBadgeAction,
}: SectionBlockProps) {
  return (
    <Accordion.Item
      value={id}
      id={`canvas-section-${id}`}
      data-testid="section-block"
      className="group space-y-2"
    >
      <div className="flex min-h-[24px] items-center justify-between">
        <Accordion.Header className="flex min-w-0 flex-1">
          <Accordion.Trigger
            data-testid={`section-block-toggle-${id}`}
            className="group/trigger flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
          >
            <CaretRight
              size={14}
              aria-hidden="true"
              data-testid={`section-block-caret-${id}`}
              className="shrink-0 text-puntt-neutral-gray-9 transition-transform duration-200 group-data-[state=open]/trigger:rotate-90"
            />
            <Text
              as="p"
              size="4"
              weight="bold"
              className="truncate text-puntt-neutral-gray-12"
              data-testid="section-block-title"
            >
              {title}
            </Text>
          </Accordion.Trigger>
        </Accordion.Header>
        {onSectionClick && (
          <button
            data-testid="section-block-ask-button"
            onClick={(e) => {
              e.stopPropagation();
              onSectionClick(id, title);
            }}
            className="ml-2 shrink-0 cursor-pointer rounded px-2 py-0.5 text-xs text-puntt-accent-9 opacity-0 transition-opacity duration-150 hover:bg-puntt-accent-2 focus:bg-puntt-accent-2 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-puntt-accent-9 group-hover:opacity-100"
          >
            Ask about this
          </button>
        )}
      </div>
      <div className="h-px w-full bg-puntt-neutral-gray-5" />
      <Accordion.Content
        data-testid={`section-block-content-${id}`}
        className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      >
        {children?.length > 0 && (
          <div className="space-y-2 pl-2 pt-2">
            {children.map((child, idx) => (
              <CanvasBlockRenderer
                key={getBlockKey(child, idx)}
                block={child}
                onSectionClick={onSectionClick}
                onBadgeAction={onBadgeAction}
              />
            ))}
          </div>
        )}
      </Accordion.Content>
    </Accordion.Item>
  );
}
